import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { API } from "../api";
import { useAuth } from "../context/AuthContext";
import { Loader2, Heart, LogOut, Mic2, ArrowRight, Bell, Ticket, QrCode, Download, Send } from "lucide-react";
import EventDayGroups from "../components/EventDayGroups";
import { applyMeta } from "../lib/meta";
import { toast } from "../hooks/use-toast";

function FanAuth({ onDone }) {
  const { login, reload } = useAuth();
  const [mode, setMode] = useState("register");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      if (mode === "register") {
        const { data } = await api.post("/auth/fan/register", form);
        localStorage.setItem("gp_token", data.token);
        await reload();
      } else {
        await login(form.email, form.password);
      }
      onDone?.();
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Something went wrong. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-md mx-auto bg-white/[0.04] border border-white/10 rounded-2xl p-7" data-testid="fan-auth">
      <div className="flex items-center gap-2 mb-1"><Heart size={20} className="text-[#8b5cf6]" /><h1 className="text-2xl font-bold text-white">Your artists, one feed</h1></div>
      <p className="text-sm text-[#8a97a8] mb-5">Create a free fan account to follow acts and see every upcoming date in one place.</p>
      <div className="flex gap-1 bg-white/5 rounded-xl p-1 mb-5">
        {["register", "login"].map((m) => (
          <button key={m} onClick={() => { setMode(m); setErr(""); }} data-testid={`fan-tab-${m}`}
            className={`flex-1 text-sm rounded-lg py-2 font-medium transition-colors ${mode === m ? "bg-[#8b5cf6] text-white" : "text-[#8a97a8] hover:text-white"}`}>
            {m === "register" ? "Sign up" : "Log in"}
          </button>
        ))}
      </div>
      <form onSubmit={submit} className="space-y-3">
        {mode === "register" && (
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="fan-name" placeholder="Your name"
            className="w-full rounded-xl bg-white/5 border border-white/15 text-white placeholder-[#6b7a90] px-4 py-2.5 outline-none focus:border-[#8b5cf6]" />
        )}
        <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="fan-email" placeholder="you@email.com"
          className="w-full rounded-xl bg-white/5 border border-white/15 text-white placeholder-[#6b7a90] px-4 py-2.5 outline-none focus:border-[#8b5cf6]" />
        <input type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="fan-password" placeholder="Password (min 6 chars)"
          className="w-full rounded-xl bg-white/5 border border-white/15 text-white placeholder-[#6b7a90] px-4 py-2.5 outline-none focus:border-[#8b5cf6]" />
        {err && <p className="text-sm text-red-400" data-testid="fan-auth-error">{err}</p>}
        <button disabled={busy} data-testid="fan-auth-submit" className="w-full rounded-xl py-3 font-medium bg-[#8b5cf6] text-white hover:bg-[#7c3aed] disabled:opacity-60 inline-flex items-center justify-center gap-2">
          {busy ? <Loader2 className="animate-spin" size={16} /> : null} {mode === "register" ? "Create account" : "Log in"}
        </button>
      </form>
    </div>
  );
}

export default function FanFeed() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [feed, setFeed] = useState(null);
  const [tab, setTab] = useState("following");
  const [tickets, setTickets] = useState(null);
  const [transfer, setTransfer] = useState(null); // { code, product_title }
  const [tForm, setTForm] = useState({ email: "", name: "" });
  const [tBusy, setTBusy] = useState(false);

  useEffect(() => { applyMeta({ title: "Your following feed · TicketsPro.app", description: "Every upcoming date from the artists you follow, in one place." }); }, []);

  const loadFeed = () => api.get("/fan/following").then(({ data }) => setFeed(data)).catch(() => setFeed({ acts: [], events: [] }));
  const loadTickets = () => api.get("/fan/tickets").then(({ data }) => setTickets(data.orders || [])).catch(() => setTickets([]));
  useEffect(() => { if (user?.role === "fan") { loadFeed(); loadTickets(); } }, [user]);

  const isFan = user?.role === "fan";
  const submitTransfer = async (e) => {
    e.preventDefault();
    if (!tForm.email.trim()) return;
    setTBusy(true);
    try {
      const { data } = await api.post(`/fan/tickets/${transfer.code}/transfer`, { email: tForm.email.trim(), name: tForm.name.trim() });
      toast({ title: "Ticket sent", description: `${transfer.code} is now with ${data.to}.` });
      setTransfer(null); setTForm({ email: "", name: "" });
      loadTickets();
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = typeof d === "string" ? d : Array.isArray(d) ? (d[0]?.msg || "Try again") : "Try again";
      toast({ title: "Couldn't transfer", description: msg, variant: "destructive" });
    } finally { setTBusy(false); }
  };
  const fmtDate = (iso) => { if (!iso) return "Date TBC"; const d = new Date(iso.length <= 10 ? iso + "T00:00" : iso); return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" }) + (iso.length > 10 ? `, ${d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}` : ""); };

  return (
    <div className="min-h-screen bg-[#0d1626]" data-testid="fan-feed-page">
      <div className="border-b border-white/10">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-white font-bold">TicketsPro.app</Link>
          {isFan && (
            <button onClick={() => { logout(); navigate("/"); }} data-testid="fan-logout" className="text-white/70 hover:text-white text-sm inline-flex items-center gap-1.5"><LogOut size={15} /> Log out</button>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-12">
        {loading ? (
          <div className="py-24 flex justify-center"><Loader2 className="animate-spin text-[#8b5cf6]" size={32} /></div>
        ) : !isFan ? (
          <FanAuth onDone={() => { loadFeed(); loadTickets(); }} />
        ) : (
          <>
            <div className="flex items-center gap-2 mb-1"><Heart size={22} className="text-[#8b5cf6]" /><h1 className="text-3xl sm:text-4xl font-bold text-white">Hi {user.name?.split(" ")[0] || "there"} 👋</h1></div>
            <p className="text-[#8a97a8] mb-6">Your artists and your tickets, all in one place.</p>

            <div className="flex gap-1 bg-white/5 rounded-xl p-1 mb-8 w-fit">
              {[["following", "Following"], ["tickets", "My tickets"]].map(([id, label]) => (
                <button key={id} onClick={() => setTab(id)} data-testid={`fan-tab-${id}`}
                  className={`text-sm rounded-lg px-5 py-2 font-medium transition-colors ${tab === id ? "bg-[#8b5cf6] text-white" : "text-[#8a97a8] hover:text-white"}`}>{label}</button>
              ))}
            </div>

            {tab === "following" ? (
              feed === null ? (
                <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-[#8b5cf6]" size={28} /></div>
              ) : (
                <>
                  {feed.acts.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-10" data-testid="fan-followed-acts">
                      {feed.acts.map((a) => (
                        <Link key={a.id} to={`/acts/${a.slug}`} data-testid={`followed-act-${a.slug}`}
                          className="inline-flex items-center gap-2 rounded-full pl-1.5 pr-3 py-1.5 bg-white/5 border border-white/10 hover:border-[#8b5cf6]/60 transition-colors">
                          <span className="w-6 h-6 rounded-full overflow-hidden bg-[#8b5cf6]/20 flex items-center justify-center text-[#c4b5fd]">
                            {a.image_url ? <img src={a.image_url} alt={a.name} className="w-full h-full object-cover" /> : <Mic2 size={12} />}
                          </span>
                          <span className="text-sm text-white">{a.name}</span>
                          <span className="text-[10px] text-[#8a97a8]">{a.upcoming}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                  {feed.events.length === 0 ? (
                    <div className="py-16 text-center rounded-2xl border border-white/10" data-testid="fan-feed-empty">
                      <Bell size={28} className="mx-auto text-[#8b5cf6]/70 mb-3" />
                      <p className="text-[#8a97a8]">{feed.acts.length === 0 ? "You're not following any artists yet." : "No upcoming dates from your artists right now — we'll keep watching."}</p>
                      <Link to="/events" className="inline-flex items-center gap-1.5 mt-4 text-[#2ec9c9] hover:underline">Discover artists <ArrowRight size={15} /></Link>
                    </div>
                  ) : (
                    <EventDayGroups events={feed.events} testidPrefix="fan" />
                  )}
                </>
              )
            ) : (
              tickets === null ? (
                <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-[#8b5cf6]" size={28} /></div>
              ) : tickets.length === 0 ? (
                <div className="py-16 text-center rounded-2xl border border-white/10" data-testid="fan-tickets-empty">
                  <Ticket size={28} className="mx-auto text-[#2ec9c9]/70 mb-3" />
                  <p className="text-[#8a97a8]">No tickets yet. Tickets you buy with <span className="text-white">{user.email}</span> will show up here — even ones bought before you signed up.</p>
                  <Link to="/events" className="inline-flex items-center gap-1.5 mt-4 text-[#2ec9c9] hover:underline">Find events <ArrowRight size={15} /></Link>
                </div>
              ) : (
                <div className="space-y-4" data-testid="fan-tickets-list">
                  {tickets.map((o) => (
                    <div key={o.order_id} data-testid={`fan-order-${o.order_id}`} className="rounded-2xl bg-white/[0.04] border border-white/10 p-5">
                      <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="text-white font-semibold text-lg">{o.product_title}</h3>
                            {o.is_upcoming ? <span className="text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full bg-[#2ec9c9]/15 text-[#2ec9c9]">Upcoming</span> : <span className="text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full bg-white/10 text-[#8a97a8]">Past</span>}
                          </div>
                          <p className="text-sm text-[#8a97a8] mt-0.5">{fmtDate(o.event_date)}{o.venue_name ? ` · ${o.venue_name}` : ""}{o.business_name ? ` · ${o.business_name}` : ""}</p>
                        </div>
                        <span className="text-white font-bold">£{o.total.toFixed(2)}</span>
                      </div>
                      <div className="mt-4 divide-y divide-white/5">
                        {o.tickets.map((t) => (
                          <div key={t.code} data-testid={`fan-ticket-${t.code}`} className="flex items-center justify-between gap-3 py-2.5">
                            <div className="min-w-0">
                              <p className="text-sm text-white truncate">{t.attendee_name || "Ticket"} <span className="text-[#8a97a8]">· {t.tier_name}{t.seat ? ` · ${t.seat}` : ""}</span></p>
                              <p className="text-xs text-[#8a97a8] font-mono">{t.code}{t.checked_in ? " · checked in" : ""}</p>
                            </div>
                            <div className="flex items-center gap-3 shrink-0">
                              <a href={`/voucher/${t.code}`} target="_blank" rel="noreferrer" data-testid={`ticket-view-${t.code}`} className="text-xs text-[#2ec9c9] hover:underline inline-flex items-center gap-1"><QrCode size={13} /> View</a>
                              <a href={`${API}/voucher/${t.code}/pdf`} target="_blank" rel="noreferrer" data-testid={`ticket-pdf-${t.code}`} className="text-xs text-[#8a97a8] hover:text-white inline-flex items-center gap-1"><Download size={13} /> PDF</a>
                              {!t.checked_in && <button onClick={() => { setTransfer({ code: t.code, product_title: o.product_title }); setTForm({ email: "", name: "" }); }} data-testid={`ticket-transfer-${t.code}`} className="text-xs text-[#c4b5fd] hover:text-white inline-flex items-center gap-1"><Send size={13} /> Transfer</button>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}
          </>
        )}
      </div>

      {transfer && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setTransfer(null)}>
          <div className="bg-[#141f33] border border-white/10 rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="transfer-modal">
            <div className="flex items-center gap-2 mb-1"><Send size={18} className="text-[#8b5cf6]" /><h3 className="text-white font-semibold text-lg">Send this ticket</h3></div>
            <p className="text-sm text-[#8a97a8] mb-4">Transfer <span className="text-white font-mono">{transfer.code}</span> ({transfer.product_title}) to a friend. It'll move to their account and leave yours.</p>
            <form onSubmit={submitTransfer} className="space-y-3">
              <input type="email" required value={tForm.email} onChange={(e) => setTForm({ ...tForm, email: e.target.value })} data-testid="transfer-email"
                placeholder="friend@email.com" className="w-full rounded-xl bg-white/5 border border-white/15 text-white placeholder-[#6b7a90] px-4 py-2.5 outline-none focus:border-[#8b5cf6]" />
              <input value={tForm.name} onChange={(e) => setTForm({ ...tForm, name: e.target.value })} data-testid="transfer-name"
                placeholder="Their name (optional)" className="w-full rounded-xl bg-white/5 border border-white/15 text-white placeholder-[#6b7a90] px-4 py-2.5 outline-none focus:border-[#8b5cf6]" />
              <div className="flex justify-end gap-2 pt-1">
                <button type="button" onClick={() => setTransfer(null)} className="text-sm text-[#8a97a8] hover:text-white border border-white/15 rounded-xl px-5 py-2.5">Cancel</button>
                <button disabled={tBusy} data-testid="transfer-submit" className="text-sm font-medium bg-[#8b5cf6] text-white hover:bg-[#7c3aed] rounded-xl px-6 py-2.5 disabled:opacity-60 inline-flex items-center gap-2">
                  {tBusy ? <Loader2 className="animate-spin" size={16} /> : <Send size={15} />} Send ticket
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
